<template>
  <span class="actuals-help-wrap">
    <b-button
      size="sm"
      variant="success"
      title="About the Daily On-Time Report"
      aria-label="Help"
      @click="show_help = true"
    >
      <i class="fa fa-info-circle"></i>
    </b-button>

    <b-modal
      v-model="show_help"
      title="Daily On-Time Report"
      size="xl"
      ok-only
      scrollable
    >
      <div class="actuals-help-body">
        <h4>What this page shows</h4>
        <p>
          One row per booking, comparing the <b>planned</b> times on the booking's vehicle assignment against the
          <b>actual</b> times calculated by actuals processing. Each milestone gets three columns: Planned, Actual, and Diff.
        </p>
        <ul>
          <li>The date at the top is the booking's <b>start day</b> — the day the trip departs. An overnight run that leaves one evening and returns the next morning appears on the day it left.</li>
          <li>Only <b>single-day</b> bookings are included (day 1 of 1). Later days of multi-day trips are skipped — use the Multiday On-Time Report for those.</li>
          <li>Bookings with no actual times at all are left out entirely, so the count here can be lower than the Actuals page count for the same day.</li>
          <li>Times display in the originating yard's timezone, falling back to the booking's stored timezone and then your company timezone.</li>
        </ul>

        <hr/>

        <h4>Where the Actual times come from</h4>
        <p>
          <b>Driver Start Time</b> comes from the driver's HOS clock-in in telematics. Every other Actual column on this
          page comes from <b>vehicle telematics position data (GPS)</b> — where the assigned vehicle was, and whether it
          was stopped or moving.
        </p>
        <p>
          Putting Driver Start next to Start Departure shows the gap between when the driver went on duty and when the
          coach actually rolled. For driver finish times, click the driver name to open that driver's HOS report for the
          day, or use the Multiday On-Time Report.
        </p>
        <p>How a GPS time gets picked for a stop:</p>
        <ul>
          <li><b>Arrival</b> — the first position report inside the stop's geofence with the vehicle stopped (speed 0). The geofence is a {{ geofence_label }} radius around the stop's coordinates by default, or the ELD-configured geofence for that address when your company has that option enabled.</li>
          <li><b>Departure</b> — after the vehicle has been sitting at the stop, the first position report showing it moving and outside the geofence.</li>
          <li>The search starts within an hour either side of the planned time and widens by an hour at a time if nothing matches.</li>
        </ul>
        <p>
          So a blank Actual usually means the vehicle never reported a stopped position near the planned coordinates —
          missing or wrong stop lat/lon, a gap in vehicle GPS reporting, or the wrong vehicle assigned — not that the
          driver never did the work.
        </p>
        <p>
          When back-to-back bookings on the same vehicle overlap, the overlap allocator narrows the start and finish
          windows so the same vehicle movement isn't credited to two bookings.
        </p>

        <hr/>

        <h4>Column by column</h4>
        <table class="actuals-help-col-table">
          <tr>
            <th>Column</th>
            <th>Planned</th>
            <th>Actual</th>
          </tr>
          <tr>
            <td><b>Driver Start Time</b></td>
            <td>Planned driver start on the assignment.</td>
            <td>Driver HOS clock-in.</td>
          </tr>
          <tr>
            <td><b>Start Departure Time</b></td>
            <td>Planned departure from the start location / yard.</td>
            <td>Vehicle's GPS-confirmed departure from the start location.</td>
          </tr>
          <tr>
            <td><b>Pickup Arrival Time</b></td>
            <td>Planned arrival at the end of the first leg — normally the first pickup stop.</td>
            <td>Vehicle's GPS-confirmed arrival at that stop.</td>
          </tr>
          <tr>
            <td><b>Pickup Departure Time</b></td>
            <td>Planned departure from the first live (revenue) leg.</td>
            <td>Vehicle's GPS-confirmed departure from the first pickup. For companies configured to use spot time as pickup time, this is the GPS arrival at the pickup instead.</td>
          </tr>
          <tr>
            <td><b>DropOff Arrival Time</b></td>
            <td>Planned arrival on the last live (revenue) leg.</td>
            <td>Vehicle's GPS-confirmed arrival at the last drop-off. For companies configured to use departure time as drop-off time, this is the GPS departure from the final leg instead.</td>
          </tr>
          <tr>
            <td><b>DropOff Departure Time</b></td>
            <td>Planned departure from the final leg.</td>
            <td>Vehicle's GPS-confirmed departure from the last drop-off.</td>
          </tr>
          <tr>
            <td><b>Finish Arrival Time</b></td>
            <td>Planned arrival back at the finish location / yard.</td>
            <td>Vehicle's GPS-confirmed arrival at the finish location.</td>
          </tr>
        </table>
        <p class="actuals-help-note">
          Pickup and drop-off columns stay blank when the booking has no live (revenue) legs identified — dead-mileage-only
          moves show start and finish times only.
        </p>

        <hr/>

        <h4>Diff, Early Limit, and Late Limit</h4>
        <ul>
          <li><b>Diff</b> is Actual minus Planned, in minutes. A positive number means the milestone happened <b>after</b> planned (late); a negative number means <b>before</b> planned (early).</li>
          <li><b>Early Limit</b> / <b>Late Limit</b> set how big a Diff has to be before it's flagged. A Diff at or beyond the limit is shaded green (early) or red (late); anything inside both limits is left plain.</li>
          <li>Changing either limit only re-shades and re-buckets the rows already loaded — it does not re-query the day.</li>
        </ul>

        <hr/>

        <h4>Filters</h4>
        <ul>
          <li>The first chip shows <b>rows displayed / rows matching your filters</b>. Click it to clear the timing filter.</li>
          <li><b>On Time</b> — no milestone on the booking crossed either limit.</li>
          <li><b>Early</b> / <b>Late</b> — <i>any</i> milestone on the booking crossed that limit. A booking that ran early to one stop and late to another counts in both, so the chip counts can add up to more than the total.</li>
          <li><b>Type</b> and <b>Base</b> narrow to a booking type or starting base. Base only appears when there's more than one to choose from.</li>
          <li><b>Filter Results</b> matches on booking ID, vehicle, or driver.</li>
          <li><b>Download</b> exports exactly the rows your current filters and limits produce.</li>
          <li>Click any column heading to sort by it; click again to reverse.</li>
        </ul>

        <hr/>

        <h4>Links out</h4>
        <ul>
          <li><b>Booking ID</b> — opens the Actuals page for that booking, where you can see the processing status and reprocess it.</li>
          <li><b>Vehicle</b> — opens that vehicle's trip history for the report date.</li>
          <li><b>Driver</b> — opens that driver's HOS report for the day (Samsara companies only). This is the place to check duty times, since no column on this page uses them.</li>
        </ul>
      </div>
    </b-modal>
  </span>
</template>

<script>
export default {
  name: 'actuals_perf_help_tbn',

  props: {
    // matches the tenant's actuals_default_geofence_meters when it differs from the 400m default
    geofence_meters: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      show_help: false,
    }
  },

  computed: {
    geofence_label() {
      const meters = parseInt(this.geofence_meters, 10)
      return `${Number.isNaN(meters) ? 400 : meters}-meter`
    },
  },
}
</script>

<style scoped>
.actuals-help-wrap {
  display: inline-block;
  margin-left: 8px;
}
.actuals-help-body {
  font-size: 15px;
  line-height: 1.5;
  text-align: left;
}
.actuals-help-body h4 {
  font-size: 17px;
  font-weight: bold;
  margin-bottom: 8px;
}
.actuals-help-body p {
  margin-bottom: 10px;
}
.actuals-help-body ul {
  padding-left: 20px;
  margin-bottom: 10px;
}
.actuals-help-body li {
  margin-bottom: 6px;
}
.actuals-help-note {
  font-size: 14px;
  font-style: italic;
  color: #6c757d;
}
.actuals-help-col-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 10px;
}
.actuals-help-col-table th,
.actuals-help-col-table td {
  border: 1px solid #ddd;
  padding: 6px 8px;
  vertical-align: top;
  font-size: 14px;
}
.actuals-help-col-table th {
  background-color: #f1f3f5;
  text-align: left;
}
.actuals-help-col-table td:first-child {
  white-space: nowrap;
}
</style>
